"use client";

import { useCallback, useEffect, useState } from "react";
import baseFetch from "./baseFetch";
import { REQUEST_METHODS } from "constants/network";
import {
  IBaseFetchRequestConfig,
  IFetchRequestConfig,
  HTTPResponseError,
  FetchResponseFormat,
} from "./type";

/**
 * useFetch hook
 *
 * @param url API end point for request
 *
 * @param requestConfig - Request config that contains body, method, headers, responseType, and init default true
 *
 * @param defaultLoadingState - To avoid extra rerendering we can send default loading state so we can skip one re-render. i.e true
 *
 * @returns  Object containing response, isLoading, fetchData and error
 */
export default function useFetch<ResponseType = unknown>(
  url: string,
  requestConfig?: IFetchRequestConfig<ResponseType>,
  defaultLoadingState = false
) {
  const [isLoading, setLoading] = useState(defaultLoadingState);
  const [response, setResponse] = useState<ResponseType | undefined>(undefined);
  const [error, setError] = useState<HTTPResponseError | unknown | undefined>(
    undefined
  );

  const {
    method = REQUEST_METHODS.GET,
    body,
    headers,
    responseType = FetchResponseFormat.JSON,
    init = true,
    domain,
    onError,
    onSuccess,
  } = requestConfig || {};

  const fetchData = useCallback(
    async (fetchConfig?: IBaseFetchRequestConfig) => {
      setLoading(true);
      try {
        const payload = {
          url,
          method,
          body,
          headers,
          domain,
          responseType,
          ...fetchConfig,
        };

        const apiResponse = await baseFetch<ResponseType>(payload);

        setResponse(apiResponse);
        onSuccess?.(apiResponse, response);
      } catch (apiError) {
        setError(apiError);
        onError?.(apiError);
      } finally {
        setLoading(false);
      }
    },
    [url]
  );

  useEffect(() => {
    if (init) fetchData();
    return () => {
      setError(undefined);
      setLoading(false);
      setResponse(undefined);
    };
  }, [fetchData, init]);

  return {
    response,
    isLoading,
    error,
    fetchData,
  };
}
